import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiCopy, FiSearch, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AvatarWrapper } from '@/components/ui/avatar';
import { ProfileResume, ProfileResumeBody, ProfileResumeName } from '@/components/custom/profile';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useSession } from '@/hooks/useSession';

const STATUS_CONFIG = {
  draft: { label: 'Rascunho', variant: 'neutral-lightest' },
  lobby: { label: 'Lobby', variant: 'warning' },
  in_progress: { label: 'Em Andamento', variant: 'primary-light' },
  finished: { label: 'Finalizado', variant: 'success-light' }
};

const STATUS_FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'lobby', label: 'Lobby' },
  { value: 'in_progress', label: 'Em Andamento' },
  { value: 'finished', label: 'Finalizado' }
];

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Badge
      variant={config.variant}
      size="xs"
    >
      {config.label}
    </Badge>
  );
}

function ChallengeRow({ challenge, onRefresh, currentUserId, isAdmin }) {
  const navigate = useNavigate();
  const { id, title, taxonomy, status, created_at, total_questions, user, is_public } = challenge;

  const formattedDate = created_at ? new Date(created_at).toLocaleDateString('pt-BR') : '—';
  const profileName = user?.profile_name;
  const isAuthor = currentUserId != null && user?.id != null && String(user.id) === String(currentUserId);
  const canManage = isAuthor || isAdmin;

  const handleLaunch = async () => {
    try {
      await liveChallengesApi.openLobby(id);
      toast.success('Lobby aberto com sucesso!');
      onRefresh();
    } catch {
      toast.error('Erro ao abrir lobby.');
    }
  };

  const handleDuplicate = async () => {
    try {
      await liveChallengesApi.duplicate(id);
      toast.success('Desafio duplicado em rascunho.');
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao duplicar desafio.';
      toast.error(message);
    }
  };

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await liveChallengesApi.destroy(id);
      toast.success('Desafio excluído.');
      setConfirmingDelete(false);
      onRefresh();
    } catch (err) {
      const message = err?.response?.data?.error || 'Erro ao excluir desafio.';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const accessCode = challenge?.access_code || '';
  const qrUrl = `${window.location.origin}/desafio-ao-vivo/entrar?code=${accessCode}`;

  return (
    <li className="hover:bg-primary-lightest/30 flex flex-col gap-3 border-b border-neutral-lightest bg-white px-5 py-3 transition-colors md:flex-row md:items-center md:gap-6">
      <div className="w-full md:w-56 md:shrink-0">
        {user ? (
          <ProfileResume>
            <AvatarWrapper
              size="sm"
              src={user.image}
              text={user.name}
              className="shrink-0"
            />
            <ProfileResumeBody>
              <ProfileResumeName title={user.name}>{user.name}</ProfileResumeName>
              {profileName && (
                <Badge
                  variant="neutral-lightest"
                  size="xs"
                  className="font-light"
                  title={profileName}
                >
                  {profileName}
                </Badge>
              )}
            </ProfileResumeBody>
          </ProfileResume>
        ) : (
          <span className="text-xs text-neutral-light">—</span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <h3 className="truncate text-sm font-semibold text-neutral-dark">{title}</h3>
          <StatusBadge status={status} />
          <Badge
            variant="neutral-lightest"
            size="xs"
            className="font-light"
          >
            {is_public ? 'Público' : 'Privado'}
          </Badge>
        </div>
        <p className="mt-0.5 text-xs text-neutral">
          {taxonomy?.name || '—'} &middot; {total_questions ?? 0} questões &middot; {formattedDate}
        </p>
      </div>

      <div className="flex flex-wrap justify-end gap-2 md:w-[450px]">
        {accessCode && canManage && challenge.status !== 'finished' && (
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={() => {
              navigator.clipboard.writeText(qrUrl);
              toast.success('Link copiado!');
            }}
          >
            <FiCopy /> Copiar link do desafio
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="xs"
          onClick={handleDuplicate}
        >
          Duplicar
        </Button>

        {canManage && status === 'draft' && (
          <>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => navigate(`/admin/desafio-ao-vivo/editar/nome/${id}`)}
            >
              Editar
            </Button>
            <Button
              type="button"
              variant="default"
              size="xs"
              onClick={handleLaunch}
            >
              Lançar
            </Button>
          </>
        )}

        {canManage && (status === 'lobby' || status === 'in_progress') && (
          <Button
            type="button"
            variant="default"
            size="xs"
            onClick={() => navigate(`/admin/desafio-ao-vivo/painel/${id}`)}
          >
            Painel
          </Button>
        )}

        {canManage && status === 'finished' && (
          <Button
            type="button"
            variant="success"
            size="xs"
            onClick={() => navigate(`/admin/desafio-ao-vivo/painel/${id}`)}
          >
            Resultados
          </Button>
        )}

        {isAuthor && (status === 'draft' || status === 'finished') && (
          <Button
            type="button"
            variant="outline-danger"
            size="xs"
            onClick={() => setConfirmingDelete(true)}
            title="Excluir desafio"
          >
            <FiTrash2 className="size-3.5" />
          </Button>
        )}
      </div>

      {confirmingDelete && (
        <ConfirmDeleteDialog
          challengeTitle={title}
          onConfirm={handleDelete}
          onCancel={() => setConfirmingDelete(false)}
          loading={deleting}
        />
      )}
    </li>
  );
}

function ConfirmDeleteDialog({ challengeTitle, onConfirm, onCancel, loading }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 px-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-dropshadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-foreground">Excluir desafio?</h3>
        <p className="mt-2 text-sm text-neutral">
          O desafio <strong>{challengeTitle}</strong> será removido permanentemente.
        </p>
        <p className="mt-2 text-xs text-neutral-light">
          Desafios duplicados a partir deste <strong>não</strong> serão afetados — eles têm cópias próprias.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Excluindo…' : 'Excluir'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LiveChallengeList() {
  const { session } = useSession();
  const currentUserId = session?.user?.id;
  const profileNames = session?.user?.profileNames || session?.user?.['profile-names'] || [];
  const isAdmin = profileNames.includes('Administrador');

  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/admin/desafio-ao-vivo',
        name: 'Desafio ao Vivo',
        isActive: true
      }
    ]
  };

  const normalizeJsonApi = items =>
    items.map(item => ({
      id: item.id,
      ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
    }));

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const response = await liveChallengesApi.index();
      const items = response?.data ?? [];
      setChallenges(normalizeJsonApi(items));
    } catch {
      toast.error('Erro ao carregar desafios ao vivo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, []);

  const filteredChallenges = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return challenges.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (normalizedSearch && !c.title?.toLowerCase().includes(normalizedSearch)) return false;
      return true;
    });
  }, [challenges, statusFilter, searchTerm]);

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <Tabs
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <TabsList>
            {STATUS_FILTERS.map(f => (
              <TabsTrigger
                key={f.value}
                value={f.value}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative md:w-72">
          <FiSearch
            aria-hidden
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-light"
          />
          <Input
            startIcon
            type="search"
            placeholder="Buscar por título..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <Button
          asChild
          variant="default"
          size="sm"
        >
          <Link to="/admin/desafio-ao-vivo/criar/nome">Criar Desafio</Link>
        </Button>
      </div>

      {loading ? (
        <Loader />
      ) : filteredChallenges.length > 0 ? (
        <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
          {filteredChallenges.map(challenge => (
            <ChallengeRow
              key={challenge.id}
              challenge={challenge}
              onRefresh={fetchChallenges}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
            />
          ))}
        </ul>
      ) : challenges.length === 0 ? (
        <EmptyState type="data" />
      ) : (
        // EmptyState não tem o type 'search' — caía no default 'Insira um texto.'
        // sem imagem. Reaproveitamos a ilustração de 'data' com texto explícito.
        <EmptyState
          type="data"
          text="Nenhum desafio encontrado para este filtro."
        />
      )}
    </DashboardContent>
  );
}
